import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  api,
  type ApiError,
  type GetMarketingEventResult,
  type ListEmbedsResult,
  type ListMarketingEventsResult,
} from '@whinerp/client';
import { formatCount, formatDateTime, instantFromWorkspaceInput, workspaceDateTimeInput } from '@whinerp/contracts/format';
import { Button, ErrorState, PageHeading, Panel, SectionLabel, Skeleton } from '@whinerp/ui';
import { useErrorMessage, useFormat, useT } from '../../app/i18n.js';
import { defineScreen } from '../types.js';
import { EventCapturePanel } from './event-capture-panel.js';

/**
 * SC-66 Event record (F-083).
 *
 * The event's settings, a registration funnel, and the attendee list. After
 * the event, attendance goes in (as the video provider reports it) and closing
 * the event splits the attendees from the no-shows and starts each path's
 * follow-up journey - nobody splits a list by hand.
 *
 * Opening the event also sends any reminders that have come due: there is no
 * scheduler in this product, and a reminder sent once per person however
 * often this screen is opened is better than one that is never sent.
 */

type Opened = GetMarketingEventResult;
type Registration = Opened['registrations'][number];
const INPUT = 'h-input rounded-control border border-control bg-surface px-3 text-body text-ink';

/** "someone@example.com, 42" per line - the shape of a provider's attendance export. */
function parseAttendance(text: string): Array<{ email: string; minutes: number }> {
  return text
    .split(/\r?\n/)
    .map((line) => line.split(/[,;\t]/).map((part) => part.trim()))
    .filter(([email]) => email !== undefined && email.includes('@'))
    .map(([email, minutes]) => ({ email: email ?? '', minutes: Number.parseFloat(minutes ?? '') || 0 }));
}

function EventRecordScreen(): JSX.Element {
  const t = useT();
  const errorText = useErrorMessage();
  const format = useFormat();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const params = useParams();
  const eventId = (params['id'] ?? 'new') === 'new' ? null : (params['id'] ?? null);

  const opened = useQuery<Opened, ApiError>({
    queryKey: ['marketing-event', eventId],
    queryFn: () => api.getMarketingEvent({ path: { eventId: eventId ?? '' }, body: {} }),
    enabled: eventId !== null,
  });
  const lists = useQuery<ListMarketingEventsResult, ApiError>({ queryKey: ['marketing-events'], queryFn: () => api.listMarketingEvents({}) });
  const forms = useQuery<ListEmbedsResult, ApiError>({ queryKey: ['embeds'], queryFn: () => api.listEmbeds({}) });

  const [name, setName] = useState('');
  const [kind, setKind] = useState<'webinar' | 'in-person'>('webinar');
  const [slug, setSlug] = useState('');
  const [description, setDescription] = useState('');
  const [startsAt, setStartsAt] = useState('');
  const [endsAt, setEndsAt] = useState('');
  const [location, setLocation] = useState('');
  const [capacity, setCapacity] = useState('');
  const [formEmbedId, setFormEmbedId] = useState('');
  const [reminders, setReminders] = useState('24, 1');
  const [attendedFlowId, setAttendedFlowId] = useState('');
  const [noShowFlowId, setNoShowFlowId] = useState('');
  const [open, setOpen] = useState(false);
  const [attendance, setAttendance] = useState('');

  useEffect(() => {
    const event = opened.data?.event;
    if (!event) return;
    setName(event.name);
    setKind(event.kind);
    setSlug(event.slug);
    setDescription(event.description ?? '');
    setStartsAt(workspaceDateTimeInput(event.startsAt, format));
    setEndsAt(workspaceDateTimeInput(event.endsAt, format));
    setLocation(event.location ?? '');
    setCapacity(event.capacity === null ? '' : String(event.capacity));
    setFormEmbedId(event.formEmbedId ?? '');
    setReminders(event.reminderHours.join(', '));
    setAttendedFlowId(event.attendedFlowId ?? '');
    setNoShowFlowId(event.noShowFlowId ?? '');
    setOpen(event.status === 'open');
  }, [opened.data]);

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ['marketing-event', eventId] });
    await queryClient.invalidateQueries({ queryKey: ['marketing-events'] });
  };
  const save = useMutation({
    mutationFn: () =>
      api.saveMarketingEvent({
        body: {
          eventId,
          name,
          kind,
          slug,
          description: description.trim() || null,
          startsAt: instantFromWorkspaceInput(startsAt, format.timeZone) ?? startsAt,
          endsAt: instantFromWorkspaceInput(endsAt, format.timeZone) ?? endsAt,
          location: location.trim() || null,
          capacity: capacity.trim() === '' ? null : Number.parseInt(capacity, 10),
          formEmbedId: formEmbedId || null,
          reminderHours: reminders
            .split(',')
            .map((part) => Number.parseInt(part.trim(), 10))
            .filter((hour) => !Number.isNaN(hour)),
          attendedFlowId: attendedFlowId || null,
          noShowFlowId: noShowFlowId || null,
          open,
        },
      }),
    onSuccess: async (saved) => {
      await refresh();
      if (eventId === null) navigate(`/events/${saved.eventId}`, { replace: true });
    },
  });
  const record = useMutation({
    mutationFn: () => api.recordEventAttendance({ path: { eventId: eventId ?? '' }, body: { entries: parseAttendance(attendance) } }),
    onSuccess: async () => {
      setAttendance('');
      await refresh();
    },
  });
  const close = useMutation({
    mutationFn: () => api.closeMarketingEvent({ path: { eventId: eventId ?? '' }, body: {} }),
    onSuccess: refresh,
  });
  const cancel = useMutation({
    mutationFn: (registrationId: string) => api.cancelEventRegistration({ path: { registrationId }, body: {} }),
    onSuccess: refresh,
  });

  if (eventId !== null && opened.isPending) return <Skeleton className="h-plot w-full" />;
  if (opened.isError) return <ErrorState title={t('state.error')} body={errorText(opened.error)} onRetry={() => void opened.refetch()} />;

  const event = opened.data?.event ?? null;
  const closed = event?.status === 'closed';
  const journeys = lists.data?.journeys ?? [];
  const captureForms = (forms.data?.embeds ?? []).filter((embed) => embed.widget === 'form' && !embed.revokedAt);
  const refusal = save.error ?? record.error ?? close.error ?? cancel.error;
  const funnel = event?.funnel;

  return (
    <div data-component="EventRecord" className="flex w-full flex-col gap-4">
      <header className="flex flex-wrap items-center gap-3">
        <PageHeading>{event ? event.name : t('events.new')}</PageHeading>
        {event ? (
          <span data-event-status className="rounded-control bg-canvas px-2 py-1 text-meta text-ink-meta">
            {t(`events.status.${event.status}`)}
          </span>
        ) : null}
        {event && event.status !== 'draft' ? (
          <a data-event-public href={event.publicPath} className="text-meta text-bronze-text hover:text-ink">
            {event.publicPath}
          </a>
        ) : null}
        <span className="ms-auto flex flex-wrap gap-2">
          {!closed ? (
            <Button intent="primary" data-event-save disabled={save.isPending} onClick={() => save.mutate()}>
              {t('events.save')}
            </Button>
          ) : null}
        </span>
      </header>
      {refusal ? (
        <p data-event-refusal className="rounded-control bg-danger-tint px-3 py-2 text-dense text-danger-text">
          {errorText(refusal)}
        </p>
      ) : null}
      {opened.data && opened.data.reminders.sent > 0 ? (
        <p data-event-reminders-sent className="text-meta text-ink-meta">
          {t('events.remindersSent', { values: { count: formatCount(opened.data.reminders.sent, format) } })}
        </p>
      ) : null}

      {event && funnel ? (
        <Panel data-region="event-funnel">
          <div className="flex flex-col gap-2">
            <SectionLabel>{t('events.funnel')}</SectionLabel>
            <ol className="flex flex-col gap-1">
              {(
                [
                  ['registered', funnel.registered],
                  ['confirmed', funnel.confirmed],
                  ['reminded', funnel.reminded],
                  ['attended', funnel.attended],
                ] as const
              ).map(([stage, count]) => (
                <li key={stage} data-funnel-stage={stage} className="flex items-center gap-2">
                  <span className="w-panel-narrow shrink-0 text-dense text-ink-600">{t(`events.stage.${stage}`)}</span>
                  <span className="h-2 flex-1 rounded-control bg-canvas">
                    <span
                      className="block h-2 rounded-control bg-bronze"
                      style={{ width: `${String(funnel.registered === 0 ? 0 : Math.round((count / funnel.registered) * 100))}%` }}
                    />
                  </span>
                  <span data-funnel-count className="w-12 text-end text-dense text-ink">{formatCount(count, format)}</span>
                </li>
              ))}
            </ol>
            {funnel.waitlisted > 0 ? (
              <p className="text-meta text-ink-meta">{t('events.waitlistCount', { values: { count: formatCount(funnel.waitlisted, format) } })}</p>
            ) : null}
            {closed ? (
              <p data-event-split className="text-dense text-ink">
                {t('events.split', { values: { attended: formatCount(funnel.attended, format), noShow: formatCount(funnel.noShow, format) } })}
              </p>
            ) : null}
          </div>
        </Panel>
      ) : null}

      <Panel data-region="event-settings">
        <fieldset disabled={closed} className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          <label className="flex flex-col gap-1 text-dense text-ink-600">
            {t('events.name')}
            <input data-event-name className={INPUT} value={name} onChange={(e) => setName(e.target.value)} />
          </label>
          <label className="flex flex-col gap-1 text-dense text-ink-600">
            {t('events.kind')}
            <select data-event-kind className={INPUT} value={kind} onChange={(e) => setKind(e.target.value as 'webinar' | 'in-person')}>
              <option value="webinar">{t('events.kind.webinar')}</option>
              <option value="in-person">{t('events.kind.in-person')}</option>
            </select>
          </label>
          <label className="flex flex-col gap-1 text-dense text-ink-600">
            {t('events.slug')}
            <input data-event-slug className={INPUT} value={slug} onChange={(e) => setSlug(e.target.value)} />
          </label>
          <label className="flex flex-col gap-1 text-dense text-ink-600">
            {kind === 'webinar' ? t('events.joinLink') : t('events.address')}
            <input data-event-location className={INPUT} value={location} onChange={(e) => setLocation(e.target.value)} />
          </label>
          <label className="flex flex-col gap-1 text-dense text-ink-600">
            {t('events.startsAt')}
            <input type="datetime-local" data-event-starts className={INPUT} value={startsAt} onChange={(e) => setStartsAt(e.target.value)} />
          </label>
          <label className="flex flex-col gap-1 text-dense text-ink-600">
            {t('events.endsAt')}
            <input type="datetime-local" data-event-ends className={INPUT} value={endsAt} onChange={(e) => setEndsAt(e.target.value)} />
          </label>
          <label className="flex flex-col gap-1 text-dense text-ink-600">
            {t('events.capacity')}
            <input data-event-capacity inputMode="numeric" className={INPUT} value={capacity} onChange={(e) => setCapacity(e.target.value)} />
          </label>
          <label className="flex flex-col gap-1 text-dense text-ink-600">
            {t('events.reminders')}
            <input data-event-reminder-hours className={INPUT} value={reminders} onChange={(e) => setReminders(e.target.value)} />
          </label>
          <label className="flex flex-col gap-1 text-dense text-ink-600">
            {t('events.form')}
            <select data-event-form className={INPUT} value={formEmbedId} onChange={(e) => setFormEmbedId(e.target.value)}>
              <option value="">{t('events.noForm')}</option>
              {captureForms.map((embed) => (
                <option key={embed.embedId} value={embed.embedId}>{embed.name}</option>
              ))}
            </select>
          </label>
          <label className="flex items-center gap-2 self-end text-dense text-ink">
            <input type="checkbox" data-event-open checked={open} onChange={(e) => setOpen(e.target.checked)} />
            {t('events.openRegistration')}
          </label>
          {journeys.length > 0 ? (
            <>
              <label className="flex flex-col gap-1 text-dense text-ink-600">
                {t('events.attendedJourney')}
                <select data-event-attended-flow className={INPUT} value={attendedFlowId} onChange={(e) => setAttendedFlowId(e.target.value)}>
                  <option value="">{t('events.noFollowUp')}</option>
                  {journeys.map((flow) => (
                    <option key={flow.flowId} value={flow.flowId}>{flow.name}</option>
                  ))}
                </select>
              </label>
              <label className="flex flex-col gap-1 text-dense text-ink-600">
                {t('events.noShowJourney')}
                <select data-event-no-show-flow className={INPUT} value={noShowFlowId} onChange={(e) => setNoShowFlowId(e.target.value)}>
                  <option value="">{t('events.noFollowUp')}</option>
                  {journeys.map((flow) => (
                    <option key={flow.flowId} value={flow.flowId}>{flow.name}</option>
                  ))}
                </select>
              </label>
            </>
          ) : (
            <p className="text-meta text-ink-meta sm:col-span-2">{t('events.noJourneys')}</p>
          )}
          <label className="flex flex-col gap-1 text-dense text-ink-600 sm:col-span-2">
            {t('events.description')}
            <textarea data-event-description rows={3} className="rounded-control border border-control bg-surface px-3 py-2 text-body text-ink" value={description} onChange={(e) => setDescription(e.target.value)} />
          </label>
        </fieldset>
      </Panel>

      {/* People met at the stand (G-163). */}
      {event ? <EventCapturePanel eventId={event.eventId} /> : null}

      {event ? (
        <Panel data-region="event-attendees">
          <div className="flex flex-col gap-2">
            <SectionLabel>{t('events.attendees')}</SectionLabel>
            {opened.data && opened.data.registrations.length > 0 ? (
              <ul data-event-attendee-list className="flex flex-col">
                {opened.data.registrations.map((row: Registration) => (
                  <li key={row.registrationId} data-attendee={row.email} className="flex flex-wrap items-center gap-2 border-t border-t-rule py-1 first:border-t-0">
                    <span className="text-dense text-ink">{row.name ?? row.email}</span>
                    <span className="text-meta text-ink-meta">{row.email}</span>
                    <span data-attendee-status className="text-meta text-ink-meta">
                      {row.attendance ? t(`events.attendance.${row.attendance}`) : t(`events.registration.${row.status}`)}
                      {row.minutesAttended ? ` · ${t('events.minutes', { values: { count: formatCount(row.minutesAttended, format) } })}` : ''}
                    </span>
                    <span className="text-meta text-ink-meta">{formatDateTime(row.registeredAt, format)}</span>
                    {!closed && row.status !== 'cancelled' ? (
                      <Button intent="ghost" size="small" data-attendee-cancel className="ms-auto" disabled={cancel.isPending} onClick={() => cancel.mutate(row.registrationId)}>
                        {t('events.cancelRegistration')}
                      </Button>
                    ) : null}
                  </li>
                ))}
              </ul>
            ) : (
              <p data-event-no-attendees className="text-meta text-ink-meta">{t('events.noAttendees')}</p>
            )}
          </div>
        </Panel>
      ) : null}

      {event && !closed ? (
        <Panel data-region="event-attendance">
          <div className="flex flex-col gap-2">
            <SectionLabel>{t('events.attendance')}</SectionLabel>
            <p className="text-meta text-ink-meta">{t('events.attendanceHelp')}</p>
            <textarea
              data-event-attendance
              rows={4}
              aria-label={t('events.attendance')}
              className="rounded-control border border-control bg-surface px-3 py-2 text-dense text-ink"
              value={attendance}
              onChange={(e) => setAttendance(e.target.value)}
            />
            {record.data && record.data.unmatched.length > 0 ? (
              <p data-event-unmatched className="text-meta text-warning-text">
                {t('events.unmatched', { values: { emails: record.data.unmatched.join(', ') } })}
              </p>
            ) : null}
            <div className="flex flex-wrap gap-2">
              <Button intent="secondary" data-event-record-attendance disabled={record.isPending || attendance.trim() === ''} onClick={() => record.mutate()}>
                {t('events.recordAttendance')}
              </Button>
              <Button intent="primary" data-event-close disabled={close.isPending} onClick={() => close.mutate()}>
                {t('events.close')}
              </Button>
            </div>
          </div>
        </Panel>
      ) : null}
    </div>
  );
}

function EventLoading(): JSX.Element {
  return <Skeleton className="h-plot w-full" />;
}
function EventError(): JSX.Element {
  const t = useT();
  return <ErrorState title={t('state.error')} body={t('events.missing')} />;
}
function EventEmpty(): JSX.Element {
  const t = useT();
  return <p className="text-body text-ink-meta">{t('events.noAttendees')}</p>;
}

export const screen = defineScreen({
  id: 'SC-66',
  route: '/events/:id',
  surface: 'page',
  title: 'Event',
  features: ['F-083'],
  entitlement: 'F-083',
  reachedBy: [{ kind: 'screen', from: 'SC-16', how: 'an event on the campaigns list' }],
  components: [],
  elements: ['EventRecord'],
  sampleRecord: [{ table: { name: 'marketing_event', idColumn: 'event_id' }, param: 'id' }],
  states: { empty: EventEmpty, loading: EventLoading, error: EventError },
  component: EventRecordScreen,
});

export default screen;
